/**
 * The ready body of the New contest dialog (ISSUE-1042 R2): the contest name,
 * the line saying whose rules were copied, the inline refusal banner, and the
 * create-pool wizard's rules step reused without Max members, which is a pool
 * setting and not part of a contest. Stateless.
 */
import { InlineBanner, Input } from "@sportsmet/ui";
import type { ReactNode, Ref } from "react";

import { CreatePoolRulesStep } from "../createPool/CreatePoolRulesStep";
import type { typeSurvivorRulesDraft } from "../createPool/survivorRulesDraft";
import {
  NEW_CONTEST_NAME_LABEL,
  NEW_CONTEST_NAME_MAX_LENGTH,
  type typeNewContestDraft,
} from "./newContestModel";

export type typeNewContestFormProps = {
  draft: typeNewContestDraft;
  nameError: string | null;
  prefillNote: string | null;
  inlineNote: string | null;
  onChangeName: (name: string) => void;
  onBlurName: () => void;
  onChangeRules: (rules: typeSurvivorRulesDraft) => void;
  /**
   * The container focuses the name field through this once the draft first
   * appears. Dialog's own autofocus runs while the body is still loading.
   */
  nameInputRef?: Ref<HTMLInputElement> | undefined;
};

export const NewContestForm = ({
  draft,
  nameError,
  prefillNote,
  inlineNote,
  onChangeName,
  onBlurName,
  onChangeRules,
  nameInputRef,
}: typeNewContestFormProps): ReactNode => {
  return (
    <div className="flex flex-col gap-6" data-testid="new-contest-form">
      {inlineNote !== null ? (
        <div data-testid="new-contest-inline-note">
          <InlineBanner variant="warning" message={inlineNote} />
        </div>
      ) : null}
      <Input
        ref={nameInputRef}
        label={NEW_CONTEST_NAME_LABEL}
        required
        maxLength={NEW_CONTEST_NAME_MAX_LENGTH}
        value={draft.name}
        error={nameError ?? undefined}
        onChange={(event) => {
          onChangeName(event.target.value);
        }}
        onBlur={onBlurName}
        data-testid="new-contest-name"
      />
      {prefillNote !== null ? (
        <p
          className="text-caption text-ink-secondary"
          data-testid="new-contest-prefill-note"
        >
          {prefillNote}
        </p>
      ) : null}
      <CreatePoolRulesStep
        rules={draft.rules}
        onChangeRules={onChangeRules}
        isMaxMembersShown={false}
      />
    </div>
  );
};
